import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import dayjs from 'dayjs';
import { get } from '../../../helpers/object-get';
import { ExcelService } from '../../../services/excel.service';
import { BaseConfig } from '../../common/config/config.adapter';
import { Align } from '../../common/types';

import { InputDirective } from '../../input/input.directive';
import { DataGridStore } from '../store/data-grid.store';
import type { ColumnDefinition } from '../types/column.type';

@Component({
  selector: 'lepi-grid-header',
  templateUrl: './grid-header.component.html',
  styleUrls: ['../mode/ssp/ssp-data-grid.component.css'],
  standalone: true,
  imports: [CommonModule, InputDirective],
})
export class GridHeader extends BaseConfig {
  private readonly excelService = inject(ExcelService);
  private readonly store = inject(DataGridStore);

  public orderBy = model<string>();
  public align = model<Align>();
  public query = model<string>('');

  public readonly columns = input.required<ColumnDefinition[]>();
  public readonly rowData = input.required<any[]>();
  public selecteds = model<any[]>([]);
  public hideExcelButton = input<boolean>(false);

  public export = output<void>();

  public readonly options = this.store.tableOptions;

  // 현재 정렬 상태
  public readonly currentOrderBy = computed(() => {
    const column = this.columns().find(
      column => (column.alias || column.field) === this.orderBy()
    );

    return column?.name || '';
  });

  private getExportData(data: any[] = this.rowData()): any[] {
    const columns = this.columns().filter(column => column.type !== 'checkbox');
    const array = data.map(item => {
      const temp = {} as any;
      for (const column of columns) {
        const value = get(item, column.field);
        let current: string | number = '';

        if (column.type === 'date') {
          current = dayjs(value).format('YYYY-MM-DD');
        } else if (column.type === 'boolean' || column.type === 'enum') {
          current = (column.badgeProps as any)?.[value]?.text || value;
        } else if (column.type === 'number') {
          current = typeof value === 'number' ? value : Number(value) || 0;
        } else {
          current = value;
        }

        if (column.formatter) {
          current = column.formatter(current, item);
        }

        temp[column.name] = current;
      }
      return temp;
    });

    return array.filter(item => item);
  }

  /**
   * @description Export 할 데이터 세팅 후 Excel로 내보냅니다.
   */
  public exportExcel(): void {
    const fileName = this.options()?.header?.excelFileName;
    if (this.options()?.header?.fetcher) {
      this.export.emit();
    } else {
      const data = this.getExportData();
      this.excelService.exportAsExcelFile(data, fileName);
    }
  }

  /**
   * @description 외부에서 받은 데이터를 엑셀로 내보냅니다.
   */
  public exportData(data: any[], fileName?: string): void {
    const processedData = this.getExportData(data);
    this.excelService.exportAsExcelFile(
      processedData,
      fileName || this.options()?.header?.excelFileName
    );
  }
}
